import { Button, Card, CardContent, CardHeader, CardTitle } from '@folio/components';
import {
  usePlannedRecoveryTasks,
  type PlannedRecoveryTask,
} from '@folio/hooks/usePlannedRecoveryTasks';
import { cn, formatDate, formatPrice } from '@folio/lib';
import { useSuspenseQuery } from '@tanstack/react-query';
import { cva } from 'class-variance-authority';
import dayjs from 'dayjs';
import { customerControllerGetCustomerBalanceQueryOptions } from 'korint-sdk';
import {
  ChevronDownIcon,
  CircleXIcon,
  EllipsisIcon,
  FileWarningIcon,
  IterationCwIcon,
  type LucideIcon,
  MailIcon,
  PauseCircleIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePolicyOnDashboard } from '../../utils/usePolicyOnDashboard';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

const CANCELLATION_WORKFLOW = 'unpaid-recovery-escalation-cancel-policy';
const EXPANDED_UPCOMING_COUNT = 3;

const WORKFLOW_ICONS: Record<string, LucideIcon> = {
  'unpaid-recovery-retry': IterationCwIcon,
  'unpaid-recovery-reminder': MailIcon,
  'unpaid-recovery-escalation-email': MailIcon,
  'unpaid-recovery-escalation-formal-notice': FileWarningIcon,
  'unpaid-recovery-escalation-suspend-policy': PauseCircleIcon,
  [CANCELLATION_WORKFLOW]: CircleXIcon,
};

const balanceCardVariants = cva('px-2 py-1 rounded-md font-semibold text-center', {
  variants: {
    status: {
      NEGATIVE: 'bg-red-100 text-red-700',
      POSITIVE: 'bg-green-100 text-green-700',
    },
  },
});

type FeedRowState = 'next' | 'upcoming' | 'end';

const feedPlateVariants = cva(
  'mt-1.5 flex h-7 w-7 flex-none items-center justify-center rounded-full',
  {
    variants: {
      state: {
        next: 'bg-red-50 text-secondary ring-[3px] ring-red-100',
        upcoming: 'border-[1.5px] border-dashed border-slate-300 bg-background text-slate-500',
        end: 'bg-red-50 text-red-700',
      },
    },
  },
);

function FeedRow({
  icon: Icon,
  state,
  title,
  subtitle,
  tag,
  isLast,
  onClick,
}: {
  icon: LucideIcon;
  state: FeedRowState;
  title: string;
  subtitle: React.ReactNode;
  tag?: string;
  isLast: boolean;
  onClick?: () => void;
}) {
  const content = (
    <>
      <Stack align="center" gap={0}>
        <span className={feedPlateVariants({ state })}>
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
        {!isLast && <span className="min-h-2 flex-1 border-l-2 border-dashed border-slate-300" />}
      </Stack>
      <Stack
        className={cn(
          'mb-1 gap-0.5 rounded-lg px-3 pb-3 pt-2 text-left',
          state === 'next' && 'bg-orange-50',
        )}
      >
        <Text
          inline
          size="sm"
          weight={state === 'next' ? 'bold' : 'semibold'}
          className={state === 'end' ? 'text-red-700' : undefined}
        >
          {title}
        </Text>
        <Text inline size="xs" tone="muted" className="tabular-nums">
          {subtitle}
        </Text>
      </Stack>
      <Text inline size="xs" weight="bold" className="pt-2.5 text-secondary">
        {tag}
      </Text>
    </>
  );
  const className = 'grid grid-cols-[28px_minmax(0,1fr)_auto] gap-x-3.5';

  return (
    <li>
      {onClick ? (
        <button type="button" className={cn(className, 'w-full')} onClick={onClick}>
          {content}
        </button>
      ) : (
        <div className={className}>{content}</div>
      )}
    </li>
  );
}

export function InvoicesBalanceModule() {
  const { t, i18n } = useTranslation();
  const { policy } = usePolicyOnDashboard();
  const { recoveryTasks } = usePlannedRecoveryTasks();
  const [planOpen, setPlanOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [nextAction] = recoveryTasks;
  const cancellation = recoveryTasks.find(
    ({ task }) => task.workflow.name === CANCELLATION_WORKFLOW,
  );

  const { data: customerBalance } = useSuspenseQuery(
    customerControllerGetCustomerBalanceQueryOptions(policy.customers[0]),
  );

  const upcoming = recoveryTasks.filter(plannedTask => plannedTask !== cancellation);
  const expandedTasks =
    upcoming.length > EXPANDED_UPCOMING_COUNT + 1
      ? upcoming.slice(0, EXPANDED_UPCOMING_COUNT)
      : upcoming;
  const hiddenCount = upcoming.length - expandedTasks.length;
  const feed: (PlannedRecoveryTask | 'more')[] = !planOpen
    ? [nextAction]
    : showAll
      ? recoveryTasks
      : [
          ...expandedTasks,
          ...(hiddenCount > 0 ? (['more'] as const) : []),
          ...(cancellation ? [cancellation] : []),
        ];

  const rowFor = (plannedTask: PlannedRecoveryTask, isLast: boolean) => {
    const { whenAt, task } = plannedTask;
    const unpaidDays = task.payload.messageData?.daysSinceFailure;
    const daysUntil = dayjs(whenAt).startOf('day').diff(dayjs().startOf('day'), 'day');
    const isNext = plannedTask === nextAction;

    return (
      <FeedRow
        key={`${task.name}-${whenAt}`}
        icon={WORKFLOW_ICONS[task.workflow.name] ?? IterationCwIcon}
        state={plannedTask === cancellation ? 'end' : isNext ? 'next' : 'upcoming'}
        title={t(`accountingDashboard.recovery.actions.${task.workflow.name}`)}
        subtitle={
          <>
            <time dateTime={whenAt}>{formatDate(new Date(whenAt), i18n.language)}</time>
            {typeof unpaidDays === 'number' &&
              ` · ${t('invoicesBalance.unpaidDays', { count: unpaidDays })}`}
          </>
        }
        tag={
          isNext
            ? daysUntil <= 0
              ? t('invoicesBalance.today')
              : t('invoicesBalance.inDays', { count: daysUntil })
            : undefined
        }
        isLast={isLast}
      />
    );
  };

  return (
    <Card>
      <CardHeader className="p-4">
        <Group justify="between" gap={6}>
          {customerBalance === null ? (
            <CardTitle className="flex align-middle text-center">
              {t('invoicesBalance.noBalance')}
            </CardTitle>
          ) : (
            <>
              <Group align="baseline" wrap gap={3}>
                <CardTitle className="flex align-middle text-center">
                  {t('invoicesBalance.balance')}
                </CardTitle>
                {nextAction && (
                  <Text inline size="sm" tone="muted">
                    {t('invoicesBalance.automaticRecovery')}
                  </Text>
                )}
              </Group>
              <div
                className={balanceCardVariants({
                  status: customerBalance.balance! >= 0 ? 'POSITIVE' : 'NEGATIVE',
                })}
              >
                {formatPrice(customerBalance.balance!)}
              </div>
            </>
          )}
        </Group>
      </CardHeader>
      {nextAction && (
        <CardContent className="flex flex-col gap-4 pt-0">
          <ul className="flex flex-col">
            {feed.map((item, index) => {
              const isLast = index === feed.length - 1;

              return item === 'more' ? (
                <FeedRow
                  key="more"
                  icon={EllipsisIcon}
                  state="upcoming"
                  title={t('invoicesBalance.moreActions', { count: hiddenCount })}
                  subtitle={t('invoicesBalance.showWholePlan')}
                  isLast={isLast}
                  onClick={() => setShowAll(true)}
                />
              ) : (
                rowFor(item, isLast)
              );
            })}
          </ul>

          <Group wrap justify="between" className="border-t pt-3.5">
            <Text size="sm" tone="muted">
              {cancellation && (
                <>
                  {t('invoicesBalance.withoutPayment')}{' '}
                  <Text inline weight="semibold" className="text-red-700">
                    {t('invoicesBalance.cancellationOn', {
                      date: formatDate(new Date(cancellation.whenAt), i18n.language),
                    })}
                  </Text>
                </>
              )}
            </Text>
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => {
                setPlanOpen(!planOpen);
                setShowAll(false);
              }}
            >
              {planOpen
                ? t('invoicesBalance.collapse')
                : t('invoicesBalance.viewPlan', { count: recoveryTasks.length })}
              <ChevronDownIcon
                className={cn('h-4 w-4 transition-transform', planOpen && 'rotate-180')}
              />
            </Button>
          </Group>
        </CardContent>
      )}
    </Card>
  );
}
